import React, { useState } from "react";
import { useGetFetchNew } from "../../data/Queries";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../components-react/ReactAlert";
import { get_banners_v4, delete_banner } from "../../data/ApiEndpoints";
import { Col, Container, Row } from "react-bootstrap";
import CommonStructured from "../common-components/CommonStructured";
import ManageBanners from "./ManageBanners";
import Modal from "react-modal";
import ModalHeadAndCloseV2 from "../payouts-wtf/common/ModalHeadAndCloseV2";
import { BinIcon1, EditIcon1, CopyIcon } from "../../images/icons/icons";
 

const BannerLanding = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddBannerModal, setShowAddBannerModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBanner, setPreviewBanner] = useState(null);
  const [previewVariant, setPreviewVariant] = useState("desktop");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [selectedGymFilter, setSelectedGymFilter] = useState(null);
  const { data, isLoading, refetch } = useGetFetchNew({
    key: "banners",
    url: get_banners_v4,
    filters: { page, is_all: 1 },
    token: userInfoData?.token,
  });

  const filterOptions = [
    { label: "All Banners", value: "all" },
    { label: "General", value: "general" },
    { label: "TV", value: "tv" },
    { label: "Website", value: "website" },
    { label: "Schedule", value: "schedule" },
  ]
  const [selectedBannerFilter, setSelectedBannerFilter] = useState(
    filterOptions[0]
  )

  // Helper function to categorize banners
  const getBannerCategory = (b) => {
    const type = (b?.type || "").toString().toLowerCase();
    const scheduleType = (b?.schedule_type || "").toString().toLowerCase();

    if (type === "schedule" || scheduleType === "schedule") return "schedule";
    if (b?.is_tv === 1 || type === "tv" || scheduleType === "tv") return "tv";
    if (b?.is_website === 1 || type.startsWith("website")) return "website";
    if (type === "general" || scheduleType === "general") return "general";
    if (b?.is_website === 1) return "website";
    if (b?.is_tv === 1) return "tv";
    return "general";
  };

  // Calculate statistics
  const bannerStats = React.useMemo(() => {
    const list = data?.data || [];
    const stats = {
      total: list.length,
      active: list.filter((b) => b.status === "active").length,
      inactive: list.filter((b) => b.status === "inactive").length,
      general: list.filter((b) => getBannerCategory(b) === "general").length,
      tv: list.filter((b) => getBannerCategory(b) === "tv").length,
      website: list.filter((b) => getBannerCategory(b) === "website").length,
      schedule: list.filter((b) => getBannerCategory(b) === "schedule").length,
    };
    return stats;
  }, [data]);

  // Filter options with counts
  const filterOptionsWithCounts = React.useMemo(() => {
    return [
      { label: "All Banners", value: "all", count: bannerStats.total },
      { label: "General", value: "general", count: bannerStats.general },
      { label: "TV", value: "tv", count: bannerStats.tv },
      { label: "Website", value: "website", count: bannerStats.website },
      { label: "Schedule", value: "schedule", count: bannerStats.schedule },
    ];
  }, [bannerStats]);

  // Build gym filter options from banner data
  const gymFilterOptions = React.useMemo(() => {
    const list = data?.data || [];
    const gymMap = new Map();
    list.forEach((b) => {
      if (b.gym_name && b.gym_id) {
        gymMap.set(b.gym_id, b.gym_name);
      }
    });
    const options = [{ label: "All Gyms", value: "all" }];
    const hasGlobal = list.some((b) => !b.gym_id || b.gym_id === "" || b.is_global === 1);
    if (hasGlobal) {
      options.push({ label: "Global (All Gyms)", value: "global" });
    }
    gymMap.forEach((name, id) => {
      options.push({ label: name, value: id });
    });
    return options;
  }, [data]);

  const filteredBanners = React.useMemo(() => {
    let list = data?.data || [];

    // Apply category filter
    if (selectedBannerFilter && selectedBannerFilter.value !== "all") {
      list = list.filter((b) => getBannerCategory(b) === selectedBannerFilter.value);
    }

    // Apply gym filter
    if (selectedGymFilter && selectedGymFilter.value !== "all") {
      if (selectedGymFilter.value === "global") {
        list = list.filter((b) => !b.gym_id || b.gym_id === "" || b.is_global === 1);
      } else {
        list = list.filter((b) => b.gym_id === selectedGymFilter.value);
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = list.filter((b) => {
        const type = (b?.type || "").toLowerCase();
        const scheduleType = (b?.schedule_type || "").toLowerCase();
        const location = (b?.location || "").toLowerCase();
        const status = (b?.status || "").toLowerCase();
        const gymName = (b?.gym_name || "").toLowerCase();
        return (
          type.includes(query) ||
          scheduleType.includes(query) ||
          location.includes(query) ||
          status.includes(query) ||
          gymName.includes(query) ||
          getBannerCategory(b).includes(query)
        );
      });
    }

    return list;
  }, [data, selectedBannerFilter, selectedGymFilter, searchQuery])

  const handleDelete = async (uid) => {
    const handleConfirm = async () => {
      setLoading(true);
      try {
        const result = await dispatch(
          commonPostPutApi({
            method: "PUT",
            endpoint: delete_banner,
            token: userInfoData?.token,
            payload: { uid },
          })
        );
        if (result?.payload?.status) {
          toast.success(
            result?.payload?.message || "Banner deleted successfully!"
          );
          refetch();
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Failed to delete banner!");
        }
      } catch (error) {
        toast.error("Something went wrong!");
      } finally {
        setLoading(false);
      }
    };

    showConfirmAlert(
      handleConfirm,
      "Are you sure you want to delete this banner?"
    );
  };

  const handleAddBanner = () => {
    setSelectedBanner(null);
    setShowAddBannerModal(true);
  };

  const handleEditBanner = (banner) => {
    setSelectedBanner(banner);
    setIsDuplicating(false);
    setShowAddBannerModal(true);
  };

  const handleDuplicateBanner = (banner) => {
    // Create a copy without uid so it creates a new banner
    const duplicatedBanner = {
      ...banner,
      uid: undefined,
      id: undefined,
    };
    setSelectedBanner(duplicatedBanner);
    setIsDuplicating(true);
    setShowAddBannerModal(true);
    toast.info("Duplicating banner - make changes and save as new");
  };

  const handleModalClose = () => {
    setShowAddBannerModal(false);
    setSelectedBanner(null);
    setIsDuplicating(false);
  };

  const handlePreviewBanner = (banner) => {
    setPreviewBanner(banner);
    setPreviewVariant("desktop");
    setShowPreviewModal(true);
  };

  const handlePreviewClose = () => {
    setShowPreviewModal(false);
    setPreviewBanner(null);
    setPreviewVariant("desktop");
  };

  return (
    <Container className="bg-cloud-k px-50px gobal-container" fluid>
      <Row>
        <Col>
          <CommonStructured
            pageHeading={"All Banners"}
            createBtnTxt={`+ Add New Banner`}
            onClickCreateBtn={handleAddBanner}
          />
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row style={{ marginTop: 24 }}>
        <Col>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
            }}
          >
            {/* Total Banners */}
            <div
              style={{
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                borderRadius: 12,
                padding: 20,
                color: "#fff",
                boxShadow: "0 4px 15px rgba(79, 70, 229, 0.25)",
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4, fontWeight: 500 }}>Total Banners</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{bannerStats.total}</div>
            </div>

            {/* Active Banners */}
            <div
              style={{
                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                borderRadius: 12,
                padding: 20,
                color: "#fff",
                boxShadow: "0 4px 15px rgba(5, 150, 105, 0.25)",
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4, fontWeight: 500 }}>Active</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{bannerStats.active}</div>
            </div>

            {/* Inactive Banners */}
            <div
              style={{
                background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                borderRadius: 12,
                padding: 20,
                color: "#fff",
                boxShadow: "0 4px 15px rgba(220, 38, 38, 0.25)",
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4, fontWeight: 500 }}>Inactive</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{bannerStats.inactive}</div>
            </div>

            {/* Website Banners */}
            <div
              style={{
                background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
                borderRadius: 12,
                padding: 20,
                color: "#fff",
                boxShadow: "0 4px 15px rgba(37, 99, 235, 0.25)",
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4, fontWeight: 500 }}>Website</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{bannerStats.website}</div>
            </div>

            {/* TV Banners */}
            <div
              style={{
                background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                borderRadius: 12,
                padding: 20,
                color: "#fff",
                boxShadow: "0 4px 15px rgba(124, 58, 237, 0.25)",
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4, fontWeight: 500 }}>TV</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{bannerStats.tv}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Search and Filter Row */}
      <Row style={{ marginTop: 24 }}>
        <Col>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Filter Tabs with Counts */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                padding: "8px",
                backgroundColor: "#dddddd",
                borderRadius: "8px",
              }}
              aria-label="Banner Type Tabs"
              role="tablist"
            >
              {filterOptionsWithCounts.map((opt) => {
                const isActive = selectedBannerFilter?.value === opt.value;
                return (
                  <button
                    key={opt.value}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setSelectedBannerFilter(opt)}
                    style={{
                      border: isActive ? "1px solid #0ea5e9" : "1px solid #d1d5db",
                      background: isActive ? "#e0f2fe" : "#ffffff",
                      color: isActive ? "#0ea5e9" : "#111827",
                      padding: "8px 14px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {opt.label}
                    <span
                      style={{
                        background: isActive ? "#0ea5e9" : "#e5e7eb",
                        color: isActive ? "#fff" : "#6b7280",
                        padding: "2px 8px",
                        borderRadius: 9999,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {opt.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Gym Filter + Search */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ position: "relative", minWidth: 200 }}>
                <select
                  value={selectedGymFilter?.value || "all"}
                  onChange={(e) => {
                    const opt = gymFilterOptions.find((o) => o.value === e.target.value);
                    setSelectedGymFilter(opt || null);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 32px 10px 36px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    fontSize: 13,
                    fontWeight: 500,
                    outline: "none",
                    appearance: "none",
                    background: selectedGymFilter && selectedGymFilter.value !== "all"
                      ? "#f0f9ff"
                      : "#fff",
                    borderColor: selectedGymFilter && selectedGymFilter.value !== "all"
                      ? "#0ea5e9"
                      : "#d1d5db",
                    color: "#111827",
                    cursor: "pointer",
                    transition: "border-color 0.2s, background 0.2s",
                  }}
                >
                  {gymFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <svg
                  style={{
                    position: "absolute",
                    left: 11,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 16,
                    height: 16,
                    pointerEvents: "none",
                  }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={selectedGymFilter && selectedGymFilter.value !== "all" ? "#0ea5e9" : "#9ca3af"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <svg
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 14,
                    height: 14,
                    pointerEvents: "none",
                    color: "#9ca3af",
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <div style={{ position: "relative", minWidth: 280 }}>
              <input
                type="text"
                placeholder="Search banners..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 16px 10px 40px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#0ea5e9")}
                onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
              />
              <svg
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 18,
                  height: 18,
                  color: "#9ca3af",
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: "#9ca3af",
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            </div>
          </div>
        </Col>
      </Row>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
          gap: 24,
          marginBlock: 32,
        }}
      >
        {isLoading ? (
          <div
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 60,
            }}
          >
            <div style={{ textAlign: "center", color: "#6b7280" }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#0ea5e9",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              Loading banners...
            </div>
          </div>
        ) : filteredBanners.length === 0 ? (
          <div
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 60,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <svg
              style={{ width: 80, height: 80, color: "#d1d5db", marginBottom: 16 }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              {searchQuery ? "No banners found" : "No banners yet"}
            </div>
            <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
              {searchQuery
                ? `No banners match "${searchQuery}". Try a different search.`
                : "Get started by creating your first banner."}
            </div>
            {!searchQuery && (
              <button
                onClick={handleAddBanner}
                style={{
                  background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
                  color: "#fff",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(14, 165, 233, 0.4)",
                }}
              >
                + Add New Banner
              </button>
            )}
          </div>
        ) : (
          filteredBanners.map((banner) => (
            <div
              key={banner.uid}
              style={{
                background: "#ffffff",
                borderRadius: 12,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 300,
                boxShadow:
                  "0 10px 30px rgba(17, 24, 39, 0.06), 0 1px 2px rgba(17, 24, 39, 0.04)",
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s"
              }}
              onClick={() => handlePreviewBanner(banner)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(17, 24, 39, 0.06), 0 1px 2px rgba(17, 24, 39, 0.04)";
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%", 
                  background: "#000",
                }}
              >
                {(() => {
                  const cardSrc = banner.image || banner.tv_banner_vertical;
                  const isVideo = banner.image
                    ? banner.media_type === "video"
                    : banner.vertical_media_type === "video";
                  return isVideo ? (
                    <video
                      src={cardSrc}
                      controls
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <img
                      src={cardSrc}
                      alt="Banner"
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        background: "#fff",
                        borderBottom:"1px solid #dddddd"
                      }}
                    />
                  );
                })()}
              </div>

              <div style={{ padding: 16,marginBlock:"16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
                  {((banner?.type || "").toLowerCase() === "tv" || (banner?.schedule_type || "").toLowerCase() === "tv" || banner?.is_tv === 1) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#0ea5e9",
                          background: "#e0f2fe",
                          padding: "4px 10px",
                          borderRadius: 9999,
                        }}
                      >
                        TV Banner
                      </span>
                      {banner?.tv_banner_horizontal && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#7c3aed",
                            background: "#ede9fe",
                            padding: "3px 8px",
                            borderRadius: 9999,
                          }}
                        >
                          Horizontal
                        </span>
                      )}
                      {banner?.tv_banner_vertical && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#059669",
                            background: "#d1fae5",
                            padding: "3px 8px",
                            borderRadius: 9999,
                          }}
                        >
                          Vertical
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{display:"flex",justifyContent:"space-between", alignItems:"center", gap:"8px"}}>
                  {(((banner?.type || "").toLowerCase().startsWith("website")) || banner?.is_website === 1) && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#0ea5e9",
                        background: "#e0f2fe",
                        padding: "4px 10px",
                        borderRadius: 9999,
                      }}
                    >
                      Website Banner
                    </span>
                  )}
                  {((((banner?.type || "").toLowerCase().startsWith("website")) || banner?.is_website === 1) && banner?.mobile_banner) && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#059669",
                        background: "#d1fae5",
                        padding: "4px 10px",
                        borderRadius: 9999,
                      }}
                    >
                      Mobile Available
                    </span>
                  )}
                  {/* GENERAL AND SCHEDULE BADGES -- use the getBannerCategory inline so only one is shown */}
                  {
                    (() => {
                      const type = (banner?.type || "").toLowerCase();
                      const schType = (banner?.schedule_type || "").toLowerCase();
                      if (type === "general" || schType === "general") {
                        return <span style={{fontSize:12,fontWeight:600,color:'#0ea5e9',background:'#e0f2fe',padding:'4px 10px',borderRadius:9999}}>General Banner</span>;
                      }
                      if (type === "schedule" || schType === "schedule") {
                        return <span style={{fontSize:12,fontWeight:600,color:'#0ea5e9',background:'#e0f2fe',padding:'4px 10px',borderRadius:9999}}>Schedule Banner</span>;
                      }
                      return null;
                    })()
                  }
                  </div>
                  {banner?.gym_name && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        width: "100%",
                        marginTop: 6,
                        padding: "6px 10px",
                        background: "#f8fafc",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span style={{ fontSize: 12, color: "#334155", fontWeight: 600, lineHeight: 1.3 }}>
                        {banner.gym_name}
                      </span>
                    </div>
                  )}
                  {!banner?.gym_name && banner?.is_global === 1 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        width: "100%",
                        marginTop: 6,
                        padding: "6px 10px",
                        background: "#f0fdf4",
                        borderRadius: 8,
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                      </svg>
                      <span style={{ fontSize: 12, color: "#15803d", fontWeight: 600 }}>
                        Global (All Gyms)
                      </span>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  {/* Edit Button */}
                  <button
                    style={{
                      color: "#374151",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 600,
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#f3f4f6",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background-color 0.2s",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditBanner(banner);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e5e7eb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                    title="Edit Banner"
                    className="banner-landing-page-icon"
                  >
                    <EditIcon1 />
                  </button>

                  {/* Duplicate Button */}
                  <button
                    style={{
                      color: "#374151",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 600,
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#f3f4f6",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background-color 0.2s",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicateBanner(banner);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#dbeafe")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                    title="Duplicate Banner"
                    className="banner-landing-page-icon"
                  >
                    <CopyIcon />
                  </button>

                  {/* Delete Button */}
                  <button
                    style={{
                      color: "#dc2626",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 600,
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#fef2f2",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background-color 0.2s",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(banner.uid);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fee2e2")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fef2f2")}
                    disabled={loading}
                    title="Delete Banner"
                    className="banner-landing-page-icon"
                  >
                    <BinIcon1 color="#dc3545" />
                  </button>
                </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={showAddBannerModal}
        onRequestClose={handleModalClose}
        ariaHideApp={false}
        contentLabel="Add/Edit Banner"
      >
        <ManageBanners
          show={showAddBannerModal}
          onHide={handleModalClose}
          isEdit={!!selectedBanner && !isDuplicating}
          initialValues={selectedBanner}
          userInfoData={userInfoData}
          onSuccess={refetch}
        />
      </Modal>

      {/* Banner Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onRequestClose={handlePreviewClose}
        ariaHideApp={false}
        contentLabel="Banner Preview"
      >
        <div className="createNewCTOModalContainer mod-width-70p">
          <ModalHeadAndCloseV2 title="Banner Preview" onClose={handlePreviewClose} />

          {/* Orientation Toggle - For TV banners with vertical version */}
          {(getBannerCategory(previewBanner) === "tv") && previewBanner?.tv_banner_vertical && (
            <div
              style={{
                display: "flex",
                background: "#f3f4f6",
                borderRadius: 8,
                padding: 4,
                width: "fit-content",
                marginBottom: 16,
              }}
            >
              <button
                onClick={() => setPreviewVariant("desktop")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  transition: "all 0.2s",
                  background: previewVariant === "desktop" ? "#fff" : "transparent",
                  color: previewVariant === "desktop" ? "#111827" : "#6b7280",
                  boxShadow: previewVariant === "desktop" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                Horizontal (1920x1080)
              </button>
              <button
                onClick={() => setPreviewVariant("vertical")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  transition: "all 0.2s",
                  background: previewVariant === "vertical" ? "#fff" : "transparent",
                  color: previewVariant === "vertical" ? "#111827" : "#6b7280",
                  boxShadow: previewVariant === "vertical" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                Vertical (1080x1920)
              </button>
            </div>
          )}

          {/* Device Toggle - Only for website banners with mobile version */}
          {((previewBanner?.type === "website") || previewBanner?.is_website === 1) && previewBanner?.mobile_banner && (
            <div
              style={{
                display: "flex",
                background: "#f3f4f6",
                borderRadius: 8,
                padding: 4,
                width: "fit-content",
                marginBottom: 16,
              }}
            >
              <button
                onClick={() => setPreviewVariant("desktop")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  transition: "all 0.2s",
                  background: previewVariant === "desktop" ? "#fff" : "transparent",
                  color: previewVariant === "desktop" ? "#111827" : "#6b7280",
                  boxShadow: previewVariant === "desktop" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                Desktop
              </button>
              <button
                onClick={() => setPreviewVariant("mobile")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  transition: "all 0.2s",
                  background: previewVariant === "mobile" ? "#fff" : "transparent",
                  color: previewVariant === "mobile" ? "#111827" : "#6b7280",
                  boxShadow: previewVariant === "mobile" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
                Mobile
              </button>
            </div>
          )}

          {/* Image/Video Container */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f9fafb",
              borderRadius: 12,
              padding: 24,
              minHeight: 300,
              border: "1px solid #e5e7eb",
            }}
          >
            {(() => {
              const previewSrc = previewVariant === "vertical" && previewBanner?.tv_banner_vertical
                ? previewBanner.tv_banner_vertical
                : previewVariant === "mobile" && previewBanner?.mobile_banner
                ? previewBanner.mobile_banner
                : previewBanner?.image;
              const isVideo = previewVariant === "vertical"
                ? previewBanner?.vertical_media_type === "video"
                : previewBanner?.media_type === "video";
              const maxWidth = previewVariant === "mobile" ? 375 : previewVariant === "vertical" ? 320 : "100%";

              return isVideo ? (
                <video
                  src={previewSrc}
                  controls
                  autoPlay
                  style={{
                    maxWidth: maxWidth,
                    maxHeight: "60vh",
                    borderRadius: 8,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                  }}
                />
              ) : (
                <img
                  src={previewSrc}
                  alt="Banner Preview"
                  style={{
                    maxWidth: maxWidth,
                    maxHeight: "60vh",
                    borderRadius: 8,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                    objectFit: "contain",
                  }}
                />
              );
            })()}
          </div>

          {/* Footer Info */}
          {previewBanner && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingTop: 16,
                marginTop: 16,
                borderTop: "1px solid #e5e7eb",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {/* Banner Type Badge */}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#fff",
                    background: "#3b82f6",
                    padding: "6px 12px",
                    borderRadius: 6,
                    textTransform: "capitalize",
                  }}
                >
                  {getBannerCategory(previewBanner)} Banner
                </span>
                {/* Media Type Badge */}
                {previewBanner.media_type && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#fff",
                      background: previewBanner.media_type === "video" ? "#7c3aed" : "#059669",
                      padding: "6px 12px",
                      borderRadius: 6,
                      textTransform: "capitalize",
                    }}
                  >
                    {previewBanner.media_type}
                  </span>
                )}
                {/* Status Badge */}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#fff",
                    background: previewBanner.status === "active" ? "#10b981" : "#6b7280",
                    padding: "6px 12px",
                    borderRadius: 6,
                    textTransform: "capitalize",
                  }}
                >
                  {previewBanner.status || "N/A"}
                </span>
              </div>
              {/* Location/Page info */}
              {previewBanner.location && (
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  Page: <span style={{ color: "#111827", fontWeight: 600 }}>{previewBanner.location}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default BannerLanding;
